/**
 * labaccess.jsx — which lab belongs to which chapter, and who may see it.
 *
 * FOUNDER, 2026-08-13: "labs stay free, thats right. but can we ultimate gateit later . only if
 * they enroll"
 *
 * ── THE ANSWER IS YES, AND THIS FILE IS WHAT MAKES IT CHEAP ───────────────────────────────────
 * Before this, the answer to "which lab shows here" was seven hardcoded lines in learn.jsx testing
 * the chapter NUMBER. Gating them later would have meant editing all seven, and every future lab
 * would have to remember to be edited too. Now the answer is a row in lab-access.json, and gating is
 * one word on that row.
 *
 * ── THE BUG THAT FELL OUT OF ASKING ───────────────────────────────────────────────────────────
 * Keying on the chapter number alone meant a lab appeared on that number of EVERY ladder. The level
 * picker makes all four reachable, so selecting Master and opening chapter 17 put the Morse light on
 * "Propulsion and Engineering Systems"; chapter 11 put a sight-reduction instrument on "Medical Care
 * in Command"; Chief Mate chapter 9 put a navigation lab on "Medical Care on Board"; and CODE101
 * chapter 1, "What Software Actually Is", got a globe. Matching on (level, chapter) fixes all of it,
 * and it is the same key gating needs — so one change closes both.
 *
 * ── A CURTAIN, NOT A LOCK, AND IT SAYS SO ─────────────────────────────────────────────────────
 * Hiding a lab here stops it being RENDERED. It does not stop it being downloaded: every lab is
 * compiled into one public bundle, so the code ships to everyone either way. If a lab ever becomes
 * the thing being sold, the honest mechanism is a dynamic import per lab plus a server that refuses
 * the chunk — until then, calling this a lock would be the commercial equivalent of inventing a
 * fact. lab-access.json states the same limitation where a founder reading policy will see it.
 */

import { useState, useEffect } from 'react';
import DECL from '../../lab-access.json';
import SEVA from '../../seva-ladders.json';
import { enrolments } from './identity.mjs';
import { labFor as rowFor, labAccess as accessFor } from './labrules.mjs';

export const DECLARATION = DECL;
export const SEVA_REGISTER = SEVA;

/* Bound to this app's declaration. The rules themselves live in labrules.mjs, with no React and no
   browser imports, so gate-lab-access.mjs can exercise the SAME functions rather than a copy of
   them — a gate that reimplements the rule it is checking proves only that I can write it twice. */
export const labFor = (level, chapter) => rowFor(DECL, level, chapter);
export const labAccess = (level, chapter) => accessFor(DECL, SEVA, level, chapter);

/**
 * The learner's enrolments — fetched ONLY when something on screen actually needs them.
 *
 * Today every row says "free", so `needed` is false everywhere and this hook never opens a request.
 * The seam costs nothing until the founder uses it, which is the point of building it now: an
 * option that slows the product down while unused is not really an option.
 */
function useEnrolled(api, level, needed) {
  const [state, setState] = useState({ loading: false, tracks: null });
  useEffect(() => {
    if (!needed) return undefined;
    let live = true;
    setState({ loading: true, tracks: null });
    enrolments(api)
      .then((r) => { if (live) setState({ loading: false, tracks: r?.enrolments || r?.tracks || [] }); })
      .catch(() => { if (live) setState({ loading: false, tracks: [] }); });
    return () => { live = false; };
  }, [api, level, needed]);
  return state;
}

/**
 * LabSlot — renders the lab declared for this (level, chapter), or nothing, or an invitation.
 *
 * `components` is passed in rather than imported here so that this module stays data-about-labs and
 * learn.jsx stays the place that owns which components exist. A declaration naming a component the
 * app does not have renders nothing rather than throwing: a policy file must not be able to white-
 * screen the learn surface, and the gate catches the mismatch on the ground where it can be fixed.
 */
export default function LabSlot({ level, chapter, components, api, gatedPanel: GatedPanel = null }) {
  const row = labFor(level, chapter);
  const access = labAccess(level, chapter);
  const needs = access === 'enrolled';
  const { loading, tracks } = useEnrolled(api, level, needs);

  if (!row) return null;
  const Lab = components?.[row.component];
  if (!Lab) return null;
  if (!needs) return <Lab />;

  if (loading) return <div className="gl-empty g-body">Checking your enrolment…</div>;
  const held = (tracks || []).some((t) =>
    String(t?.track || t).toUpperCase() === String(level).toUpperCase());
  if (held) return <Lab />;

  /* The same invitation the gated chapters show, so Learn does not grow a second, different promise
     about what enrolment buys. Falls back to plain text if no panel was supplied. */
  const envelope = {
    state: 'gated', chapter: row.chapter, title: row.chapter_title,
    reason: 'This lab is for enrolled learners.',
    free_instead: 'The chapter\'s own reading and practice follow their ladder\'s free tier.',
    named: false,
  };
  return GatedPanel
    ? <div style={{ marginTop: 'var(--g-3)' }}><GatedPanel envelope={envelope} fallbackTitle={row.chapter_title} what="This lab" /></div>
    : <p className="g-body g-dim">This lab is for enrolled learners.</p>;
}
